import { BookOpen, Building2, Handshake, Heart, Users } from "lucide-react";

import type { UserCapabilitiesOut } from "@/services/api";
import type { LucideIcon } from "lucide-react";

export type ExpertCapabilityKey = keyof Pick<
  UserCapabilitiesOut,
  | "joinPabBoard"
  | "createLeadPrograms"
  | "createLeadPeerPrograms"
  | "createLeadOrganisationPrograms"
  | "joinAsCoExpert"
  | "peerAdvisory"
  | "coFounding"
>;

/**
 * The "How do you want to contribute as an Expert?" options, each a boolean
 * column on user_capabilities. Titles and descriptions follow Figma node
 * 315:352 (Expert signup step 3, Operator DNA) -- the signup form, the
 * application wizard and the profile's editable card all render these.
 */
export const CONTRIBUTION_OPTIONS: {
  name: ExpertCapabilityKey;
  title: string;
  description: string;
}[] = [
  {
    name: "joinPabBoard",
    title: "Join Personal Advisory Boards",
    description:
      "Serve on an achiever's Personal Advisory Board as one of their curated board of Experts for a 12-week journey.",
  },
  {
    name: "createLeadPrograms",
    title: "Create and Lead Expert Programs",
    description:
      "Design and facilitate structured programs for cohorts of achievers around your expertise area.",
  },
  {
    name: "createLeadPeerPrograms",
    title: "Create and Lead Peer Programs",
    description:
      "Exchange expertise and support with peers at similar career levels in a structured format.",
  },
  {
    name: "createLeadOrganisationPrograms",
    title: "Create and Lead Organization Programs",
    description:
      "Partner with other experts or achievers on early-stage ventures or entrepreneurial projects.",
  },
];

/**
 * "Primary Goal for Joining" (Figma node 315:352), stored as
 * user_capabilities.contribution_goals. Titles only -- the design's
 * descriptions were placeholders. Choosing ADVISE also ticks "Join Personal
 * Advisory Boards", since advising is what a PAB seat is.
 */
export const PRIMARY_GOAL_ADVISE = "advise";
export const PRIMARY_GOALS: { value: string; title: string }[] = [
  { value: "cofound", title: "Co-found a new venture" },
  { value: "fractional", title: "Take on fractional or interim projects" },
  { value: PRIMARY_GOAL_ADVISE, title: "Advise high-potential startups and leaders" },
];

export const primaryGoalTitle = (value: string): string =>
  PRIMARY_GOALS.find((goal) => goal.value === value)?.title ?? value;

/**
 * Every contribution flag as a role, for the profile's "Applied Capabilities"
 * list and the admin review drawer -- two views of one flag, so they name it
 * identically.
 *
 * The first four are what signup asks today. The last three are no longer
 * asked but are kept for Experts who chose them earlier; both lists render
 * only flags that are true, so new Experts never see them.
 */
export const EXPERT_CAPABILITIES: {
  key: ExpertCapabilityKey;
  label: string;
  icon: LucideIcon;
  description: string;
}[] = [
  {
    key: "joinPabBoard",
    label: "Personal Advisory Board Expert",
    icon: Heart,
    description:
      "Serve on an achiever's Personal Advisory Board as one of their curated board of Experts for a 12-week journey.",
  },
  {
    key: "createLeadPrograms",
    label: "Program Creator",
    icon: BookOpen,
    description:
      "Design and facilitate structured programs for cohorts of achievers around your expertise area.",
  },
  {
    key: "createLeadPeerPrograms",
    label: "Peer Program Creator",
    icon: Users,
    description:
      "Exchange expertise and support with peers at similar career levels in a structured format.",
  },
  {
    key: "createLeadOrganisationPrograms",
    label: "Organization Program Creator",
    icon: Building2,
    description:
      "Partner with other experts or achievers on early-stage ventures or entrepreneurial projects.",
  },
  {
    key: "joinAsCoExpert",
    label: "Co-Expert",
    icon: Users,
    description:
      "Collaborate with program leads to add depth, run sessions, or provide specialist support.",
  },
  {
    key: "peerAdvisory",
    label: "Peer Advisory",
    icon: Users,
    description:
      "Exchange expertise and support with peers at similar career levels in a structured format.",
  },
  {
    key: "coFounding",
    label: "Co-founder Opportunities",
    icon: Handshake,
    description:
      "Partner with other experts or achievers on early-stage ventures or entrepreneurial projects.",
  },
];

/**
 * The acknowledgements every Expert gives, whichever route they took.
 *
 * Someone becomes an Expert either by signing up as one (/signup/3) or by
 * applying from an achiever account (the apply-as-expert wizard). Both ask for
 * the same four, so the wording lives here rather than in either flow — two
 * copies would let the two populations consent to different text.
 */
export type ExpertConsentKey = keyof Pick<
  UserCapabilitiesOut,
  | "infoAccuracyConfirmed"
  | "privacyPolicyAcknowledged"
  | "applicationReviewConsented"
  | "profileUseConsented"
>;

/**
 * The four the user actually ticks. `profileBuildConsented` is a fifth column
 * with no checkbox of its own: it restates the meaning of the first, and both
 * flows send it as true alongside these.
 */
export const EXPERT_CONSENTS: { key: ExpertConsentKey; label: string }[] = [
  {
    key: "infoAccuracyConfirmed",
    label: "I confirm that all information provided is accurate and up to date.",
  },
  {
    key: "privacyPolicyAcknowledged",
    label: "I acknowledge the AchieveOS privacy policy and data handling practices.",
  },
  {
    key: "applicationReviewConsented",
    label: "I consent to my application being reviewed by the Forward team.",
  },
  {
    key: "profileUseConsented",
    label: "If approved, I consent to my Expert Profile being used for Achiever matching.",
  },
];

/** All four unticked — the starting state of a consent block. */
export const emptyExpertConsents = (): Record<string, boolean> =>
  Object.fromEntries(EXPERT_CONSENTS.map(({ key }) => [key, false]));
